import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { ProgressBar } from "primereact/progressbar";
import { emojify } from "node-emoji";
import toast from "react-hot-toast";
import { useDebouncedUsersQuery } from "../../hooks/useDebouncedUsersQuery";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useGetEventsQuery } from "../../store/services/events";
import { DateBodyTemplate, UserPhoneBodyTemplate } from "../../components";
import { User } from "../../models/users";
import { useSendPushNotificationMutation } from "../../store/services/notifications";
import { BatchNotificationResponse, PushNotificationPayload, PushNotificationRedirectPayload } from "../../models/notifications";
import { useDebounce } from "../../hooks/useDebounce";
import { isUserRestrictedByBan } from "../../utils/functions/users";
import { useNavigate } from "react-router-dom";

export const SendPushNotifications: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = React.useState<string>("");
  const [content, setContent] = React.useState<string>("");
  const [eventId, setEventId] = React.useState<number>(0);
  const [eventSearch, setEventSearch] = React.useState<string>("");
  const debouncedEventSearch = useDebounce(eventSearch, 500);

  const trimmedEventSearch = debouncedEventSearch.trim();
  const shouldFetchEvents = trimmedEventSearch.length > 0;
  const isEventIdSearch = /^\d+$/.test(trimmedEventSearch);

  const { data: eventsPage, isLoading: isLoadingEvents } = useGetEventsQuery(
    shouldFetchEvents
      ? isEventIdSearch
        ? { id: Number(trimmedEventSearch), limit: 50 }
        : { name: trimmedEventSearch, limit: 50 }
      : { name: "", limit: 1 },
    { skip: !shouldFetchEvents }
  );
  const events = eventsPage?.events;

  const eventOptions = React.useMemo(() => {
    const defaultOption = { id: 0, name: "General (Sin evento específico)" };
    if (!events) return [defaultOption];
    return [
      defaultOption,
      ...events.map(event => ({
        ...event,
        name: `${event.name} - ${event.id}`,
      }))
    ];
  }, [events]);

  const [userId, setUserId] = React.useState<string>();
  const [userEmail, setUserEmail] = React.useState<string>();
  const [userFirstName, setUserFirstName] = React.useState<string>();
  const [userLastName, setUserLastName] = React.useState<string>();
  const [userTenantId, setUserTenantId] = React.useState<string | null>();

  const [selectedUsersMap, setSelectedUsersMap] = React.useState<Map<number, User>>(new Map());
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [isSending, setIsSending] = React.useState<boolean>(false);
  const [sendOutcome, setSendOutcome] = React.useState<{ unmatchedEmails?: string[]; errorMessage?: string } | null>(null);
  const [sendPush] = useSendPushNotificationMutation();
  const [totalToSend, setTotalToSend] = React.useState<number>(0);
  const [processedCount, setProcessedCount] = React.useState<number>(0);

  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});

  const tenantOptions = React.useMemo(() => {
    return tenants || [];
  }, [tenants]);

  const { data: usersData, isLoading, refetch } = useDebouncedUsersQuery({
    id: userId,
    email: userEmail,
    firstName: userFirstName,
    lastName: userLastName,
    userTypeId: 2,
    tenantId: userTenantId,
  });

  const communicationEligibleUsers = React.useMemo(
    () => (usersData ? usersData.filter((user) => !isUserRestrictedByBan(user)) : []),
    [usersData]
  );

  const excludedUsersCount = React.useMemo(() => {
    if (!usersData) return 0;
    return usersData.length - communicationEligibleUsers.length;
  }, [usersData, communicationEligibleUsers.length]);

  React.useEffect(() => {
    setSelectedUsersMap((prev) => {
      let changed = false;
      const next = new Map(prev);
      next.forEach((user, id) => {
        if (isUserRestrictedByBan(user)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [usersData]);

  const toggleSelectUser = (user: User) => {
    if (isUserRestrictedByBan(user)) {
      toast.error("No se puede seleccionar un usuario con restricción de acceso");
      return;
    }
    setSelectedUsersMap(prev => {
      const next = new Map(prev);
      if (next.has(user.id)) next.delete(user.id); else next.set(user.id, user);
      return next;
    });
  };

  const allFilteredIds = React.useMemo(
    () => communicationEligibleUsers.map((user) => user.id),
    [communicationEligibleUsers]
  );
  const allSelected = React.useMemo(
    () => allFilteredIds.length > 0 && allFilteredIds.every(id => selectedUsersMap.has(id)),
    [allFilteredIds, selectedUsersMap]
  );

  const toggleSelectAllFiltered = () => {
    setSelectedUsersMap(prev => {
      const next = new Map(prev);
      if (allSelected) {
        // Unselect all filtered ids
        allFilteredIds.forEach(id => next.delete(id));
      } else {
        // Select all filtered ids
        communicationEligibleUsers.forEach(user => next.set(user.id, user));
      }
      return next;
    });
  };

  const validateAndConfirm = () => {
    if (!title || title.trim() === "") {
      toast.error("Falta el título");
      return;
    }
    if (!content || content.trim() === "") {
      toast.error("Falta el contenido");
      return;
    }
    if (selectedUsersMap.size === 0) {
      toast.error("Debe seleccionar al menos un usuario");
      return;
    }
    setShowConfirm(true);
  };

  const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const handleSendPushNotifications = async () => {
    setIsSending(true);
    setSendOutcome(null);
    try {
      const selected = Array.from(selectedUsersMap.values());
      const toEmails = Array.from(
        new Set(
          selected
            .map(u => u.email)
            .filter((e): e is string => !!e && e.trim() !== "")
            .map(e => e.trim())
        )
      );

      if (toEmails.length === 0) {
        const msg = "Los usuarios seleccionados no tienen emails válidos";
        setSendOutcome({ errorMessage: msg });
        toast.error(msg);
        return;
      }

      setTotalToSend(toEmails.length);
      setProcessedCount(0);

      const sendChunk = async (emailsChunk: string[]) => {
        const basePayload: PushNotificationPayload = {
          userEmails: emailsChunk,
          title: emojify(title.trim()),
          body: emojify(content.trim()),
        };
        const payload: PushNotificationPayload | PushNotificationRedirectPayload = eventId > 0
          ? { ...basePayload, data: { screen: "EventDetail", params: { eventId } } }
          : basePayload;
        const response = await sendPush(payload).unwrap();
        const maybeData = response?.data as BatchNotificationResponse | PushNotificationPayload | undefined;
        const unmatched = (maybeData as BatchNotificationResponse)?.unmatchedEmails;
        const list = Array.isArray(unmatched) ? unmatched : [];
        return { unmatched: list, message: (response as any)?.message as string | undefined };
      };

      let aggregatedUnmatched: string[] = [];
      let lastMessage: string | undefined = undefined;

      if (toEmails.length <= 100) {
        const { unmatched, message } = await sendChunk(toEmails);
        aggregatedUnmatched = unmatched;
        lastMessage = message;
        setProcessedCount(toEmails.length);
      } else {
        const chunkSize = 80;
        for (let i = 0; i < toEmails.length; i += chunkSize) {
          const chunk = toEmails.slice(i, i + chunkSize);
          const { unmatched, message } = await sendChunk(chunk);
          if (unmatched.length) {
            aggregatedUnmatched = aggregatedUnmatched.concat(unmatched);
          }
          lastMessage = message ?? lastMessage;
          setProcessedCount((prev) => Math.min(prev + chunk.length, toEmails.length));
          if (i + chunkSize < toEmails.length) {
            await sleep(3000);
          }
        }
      }

      setSendOutcome({ unmatchedEmails: aggregatedUnmatched });
      if (aggregatedUnmatched.length > 0) {
        toast.success(lastMessage ?? `Enviadas. ${aggregatedUnmatched.length} email(s) sin token: ${aggregatedUnmatched.join(", ")}`);
      } else {
        toast.success(lastMessage ?? "Push notifications enviadas correctamente");
      }
      setSelectedUsersMap(new Map());
    } catch (e: any) {
      const apiError = e;
      let errMsg = apiError?.data?.message
        || apiError?.data?.error
        || apiError?.data
        || apiError?.error
        || apiError?.message
        || (apiError?.status ? `HTTP ${apiError.status} error` : undefined)
        || "No se pudieron enviar las notificaciones";
      if (typeof errMsg === "object") {
        try { errMsg = JSON.stringify(errMsg); } catch {}
      }
      console.error("Push notification send error:", apiError);
      setSendOutcome({ errorMessage: String(errMsg) });
      toast.error(String(errMsg));
    } finally {
      setIsSending(false);
    }
  };

  const RealUsersheader = (
    <div className="grid mt-4">
      <div className="col-12 md:col-2 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="id"
            name="id"
            onChange={(e) => {
              const rawValue = e.currentTarget.value;
              const numericOnly = rawValue.replace(/\D+/g, "");
              setUserId(numericOnly);
            }}
            value={userId}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <label htmlFor="id">ID</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setUserId("");
            refetch();
          }}
        />
      </div>
      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="email"
            name="email"
            onChange={(e) => setUserEmail(e.currentTarget.value)}
            value={userEmail}
          />
          <label htmlFor="email">Email</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setUserEmail("");
            refetch();
          }}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="lastName"
            name="lastName"
            onChange={(e) => setUserLastName(e.currentTarget.value)}
            value={userLastName}
          />
          <label htmlFor="lastName">Apellido</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setUserLastName("");
            refetch();
          }}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="firstName"
            name="firstName"
            onChange={(e) => setUserFirstName(e.currentTarget.value)}
            value={userFirstName}
          />
          <label htmlFor="firstName">Nombre</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setUserFirstName("");
            refetch();
          }}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="userTenantId"
            name="userTenantId"
            onChange={(e) => {
              setUserTenantId(e.value);
              setTimeout(() => refetch(), 0);
            }}
            value={userTenantId}
            optionLabel="provider"
            optionValue="id"
            options={tenantOptions}
            loading={isLoadingTenants}
            placeholder="Seleccionar tenant"
            showClear
          />
          <label htmlFor="userTenantId">Tenant</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setUserTenantId(undefined);
            refetch();
          }}
        />
      </div>
    </div>
  );

  const selectedUsers: User[] = React.useMemo(() => {
    return Array.from(selectedUsersMap.values());
  }, [selectedUsersMap]);

  const tableData = React.useMemo(() => {
    return [...communicationEligibleUsers];
  }, [communicationEligibleUsers]);

  return (
    <>
      <div className="p-card gap-4 p-3">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-3">
          <h1 className="text-center">Enviar Push notifications</h1>
          <Button
            icon="pi pi-history"
            rounded
            outlined
            size="small"
            type="button"
            onClick={() => navigate("/notifications/history")}
            aria-label="Historial Push"
            tooltip="Historial Push"
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
        <div className="grid">
          <div className="col-12 md:col-6">
            <span className="p-float-label w-full">
              <InputText
                id="pushTitle"
                className="w-full"
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
              />
              <label htmlFor="pushTitle">Título</label>
            </span>
          </div>
          <div className="col-12 md:col-6 mt-3 md:mt-0">
            <div className="p-inputgroup">
              <span className="p-float-label w-full">
                <Dropdown
                  id="pushEvent"
                  className="w-full"
                  value={eventId}
                  onChange={(e) => setEventId(e.value)}
                  options={eventOptions}
                  optionLabel="name"
                  optionValue="id"
                  filter
                  onFilter={(e) => setEventSearch(e.filter)}
                  loading={isLoadingEvents}
                />
                <label htmlFor="pushEvent">Evento Relacionado (Opcional)</label>
              </span>
              <Button
                icon="pi pi-times"
                className="p-button-danger"
                onClick={() => {
                  setEventId(0);
                  setEventSearch("");
                }}
              />
            </div>
          </div>
          <div className="col-12 mt-3">
            <span className="p-float-label w-full">
              <InputTextarea
                id="pushContent"
                className="w-full"
                autoResize
                rows={4}
                value={content}
                onChange={(e) => setContent((e.target as HTMLTextAreaElement).value)}
              />
              <label htmlFor="pushContent">Contenido</label>
            </span>
          </div>
        </div>
        <div className="flex justify-content-end mt-2">
          <Button
            label="Enviar push notification"
            icon="pi pi-send"
            onClick={validateAndConfirm}
            disabled={isSending}
          />
        </div>
      </div>

      <div className="p-card gap-4 p-3 mt-4">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-2 mb-4">
          <h2 className="text-center m-0">Usuarios</h2>
        </div>
        {excludedUsersCount > 0 && (
          <div className="mb-3 text-orange-600 font-medium">
            Se excluyeron automáticamente {excludedUsersCount} usuario(s) con restricción de acceso.
          </div>
        )}
        <DataTable
          key={selectedUsersMap.size}
          value={tableData}
          loading={isLoading}
          header={RealUsersheader}
          tableStyle={{ minWidth: "60rem" }}
          rowClassName={(rowData: User) => ({ "surface-100": selectedUsersMap.has(rowData.id) })}
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25, 50]}
        >
          <Column
            header={
              <div className="flex justify-content-center">
                <Checkbox onChange={toggleSelectAllFiltered} checked={allSelected} />
              </div>
            }
body={(rowData: User) => (
              <div className="flex justify-content-center">
                <Checkbox
                  inputId={`select-user-${rowData.id}`}
                  onChange={() => toggleSelectUser(rowData)}
                  checked={selectedUsersMap.has(rowData.id)}
                  className={selectedUsersMap.has(rowData.id) ? 'p-highlight' : ''}
                />
              </div>
            )}
          />
          <Column field="id" header="ID"></Column>
          <Column field="email" header="Email"></Column>
          <Column field="lastName" header="Apellido"></Column>
          <Column field="firstName" header="Nombre"></Column>
          <Column field="userPhone" header="Telefono" body={UserPhoneBodyTemplate}></Column>
          <Column field="createDate" header="Fecha de creación" body={DateBodyTemplate}></Column>
          <Column
            field="tenants"
            header="Tenant"
            body={(rowData: User) => (rowData.tenants && rowData.tenants.length > 0 ? rowData.tenants.map(t => t.provider).join(", ") : "-")}
          />
          <Column
            field="banned"
            header="Ban"
            body={(rowData: User) => (
              <i className={`pi ${isUserRestrictedByBan(rowData) ? 'pi-ban text-red-500' : 'pi-check text-green-500'}`} style={{ fontSize: '1.2rem' }} title={isUserRestrictedByBan(rowData) ? 'Usuario restringido' : 'Usuario activo'} />
            )}
          />
        </DataTable>
      </div>

      <Dialog
        header="PRECAUCION: Enviar push notification?"
        visible={showConfirm}
        style={{ width: "50vw" }}
        onHide={() => { setShowConfirm(false); setSendOutcome(null); }}
        footer={
          sendOutcome ? null : (
            <div>
              <Button
                severity="success"
                label={isSending ? "Enviando..." : "Enviar"}
                icon={isSending ? "pi pi-spin pi-spinner" : "pi pi-send"}
                disabled={isSending}
                onClick={handleSendPushNotifications}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={() => setShowConfirm(false)}
                disabled={isSending}
              />
            </div>
          )
        }
      >
        <div className="mb-3">
          <h1 className="text-3xl text-red-500 m-0 p-0">
            <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
          </h1>
        </div>
        <div className="mb-2"><b>Título:</b> {emojify(title)}</div>
        <div className="mb-2"><b>Contenido:</b> {emojify(content)}</div>
        {eventId > 0 && (
          <div className="mb-2"><b>Evento:</b> {events?.find(e => e.id === eventId)?.name || `ID: ${eventId}`}</div>
        )}
        <div className="mb-2">
          <b>Usuarios seleccionados ({selectedUsers.length}):</b>
          <ul className="m-0 pl-3">
            {selectedUsers.map(u => (
              <li key={u.id}>{u.firstName} {u.lastName} ({u.email})</li>
            ))}
          </ul>
        </div>
        {sendOutcome && (
          <div className="mt-3">
            <h3 className="mt-0">Resultado del envío</h3>
            {totalToSend > 0 && (
              <div className="mb-3">
                <div className="mb-1">Progreso: {processedCount} / {totalToSend}</div>
                <ProgressBar value={Math.round((processedCount / totalToSend) * 100)}></ProgressBar>
              </div>
            )}
            {sendOutcome.errorMessage ? (
              <div className="text-red-600">{sendOutcome.errorMessage}</div>
            ) : sendOutcome.unmatchedEmails && sendOutcome.unmatchedEmails.length > 0 ? (
              <>
                <div className="mb-2"><b>Emails sin token:</b></div>
                <ul className="m-0 pl-3">
                  {sendOutcome.unmatchedEmails.map((email) => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="text-green-600">Todos los usuarios coinciden con tokens.</div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
};
